import { useState } from "react";
import { useHistory } from "react-router";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

// Calls
import {
  billingRequest,
  resumeSubscribeRequest
} from "../../../../api/calls/subscription.call";

// Actions
import { fetchActiveSubscription } from "../../../../redux/actions/business.actions";

// Billing UI
import { CancelModal } from "./PlanModals";
import { AddCardModal, ChangeDefaultModal } from "./CardModals";
import {
  Banner,
  money,
  fmtStamp,
  fmtStampShort,
  daysUntilStamp,
  locationsLabel
} from "./shared";
import { I, CardBrand, cardLabel } from "./icons";

// Utils
import { isEmpty } from "../../../../utils";

const PanelShell = ({ badge, children }) => (
  <div className="panel">
    <div className="panel-head">
      <div className="panel-eyebrow">Subscription</div>
      {badge}
    </div>
    <div className="panel-body">{children}</div>
  </div>
);

const SubscriptionPanel = ({ activeSubscription, cards, onRefreshCards }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [modal, setModal] = useState(null); // 'cancel' | 'addCard' | 'changeDefault'

  const plan = activeSubscription?.item?.subscriptionPlan;
  const price = activeSubscription?.item?.subscriptionPrice;
  const defaultCard = cards.find(card => card.isDefault) || cards[0] || null;
  const isCanceled =
    !isEmpty(activeSubscription?.canceledAt) ||
    !isEmpty(activeSubscription?.cancelAt);

  const refresh = () => setTimeout(() => dispatch(fetchActiveSubscription()), 1000);

  const resume = async () => {
    const response = await resumeSubscribeRequest();
    if (response.hasError) {
      toast.error(response.error.message);
      return;
    }
    toast.success("Subscription resumed.");
    refresh();
  };

  const openPortal = async () => {
    const response = await billingRequest();
    if (response.hasError) {
      toast.error(response.error.message);
      return;
    }
    window.open(response.redirectUrl, "_blank", "noopener");
  };

  const modals = (
    <>
      {modal === "cancel" && (
        <CancelModal
          activeSubscription={activeSubscription}
          onClose={() => setModal(null)}
        />
      )}
      {modal === "addCard" && (
        <AddCardModal onClose={() => setModal(null)} onSaved={onRefreshCards} />
      )}
      {modal === "changeDefault" && (
        <ChangeDefaultModal
          cards={cards}
          onClose={() => setModal(null)}
          onChanged={onRefreshCards}
        />
      )}
    </>
  );

  /* 1 — no subscription */
  if (isEmpty(activeSubscription)) {
    return (
      <PanelShell>
        <div className="bp-empty">
          <div className="bp-empty-ico">
            <I.Card width="22" height="22" />
          </div>
          <div className="bp-empty-title">
            Select a subscription plan to unlock features
          </div>
          <div className="bp-empty-text">
            Pick a plan to start receiving service bookings, manage your team
            and list your shop in the driver app.
          </div>
          <button
            className="btn btn-primary"
            onClick={() => history.push("/subscription-plans")}
          >
            Choose a plan
          </button>
        </div>
      </PanelShell>
    );
  }

  const endStamp = activeSubscription.currentPeriodEnd;
  const monthly = price ? `${money(price.unitAmount)}/mo` : null;

  /* 4 — canceled, access until period end */
  if (isCanceled) {
    return (
      <>
        <PanelShell
          badge={
            <span className="badge badge-warn">
              <I.Clock width="12" height="12" /> Canceling
            </span>
          }
        >
          <Banner
            variant="warn"
            icon={<I.Alert width="18" height="18" />}
            title={`Your subscription ends on ${fmtStamp(endStamp)}`}
            text="You'll keep access to your branches and bookings until then. Resume anytime before that date to stay subscribed."
          />
          <div className="bp-plan-row" style={{ marginTop: 16 }}>
            <div>
              <div className="bp-plan-name">{plan?.name}</div>
              <div className="bp-plan-sub">
                Access until {fmtStampShort(endStamp)}
              </div>
            </div>
          </div>
          <div className="bp-actions">
            <button className="btn btn-primary" onClick={resume}>
              Resume subscription
            </button>
          </div>
        </PanelShell>
        {modals}
      </>
    );
  }

  /* 5 — past due */
  if (activeSubscription.status === "past_due") {
    return (
      <>
        <PanelShell
          badge={
            <span className="badge badge-error">
              <I.Alert width="12" height="12" /> Past due
            </span>
          }
        >
          <Banner
            variant="error"
            icon={<I.Alert width="18" height="18" />}
            title="We couldn't charge your card"
            text="Update your payment method to keep access to your account."
          />
          <div className="bp-plan-row" style={{ marginTop: 16 }}>
            <div>
              <div className="bp-plan-name">{plan?.name}</div>
              <div className="bp-plan-sub">
                {monthly}
                {defaultCard ? <> · {cardLabel(defaultCard)}</> : null}
              </div>
            </div>
          </div>
          <div className="bp-actions">
            <button
              className="btn btn-primary"
              onClick={() => setModal("addCard")}
            >
              Update card
            </button>
          </div>
        </PanelShell>
        {modals}
      </>
    );
  }

  /* 2 — trialing */
  if (activeSubscription.status === "trialing") {
    const daysLeft = daysUntilStamp(endStamp);
    const totalDays = plan?.trialPeriodDays || 90;
    const soon = daysLeft <= 7;
    const progress = Math.min(
      100,
      Math.max(0, Math.round(((totalDays - daysLeft) / totalDays) * 100))
    );
    return (
      <>
        <PanelShell
          badge={
            <span className={"badge " + (soon ? "badge-warn" : "badge-accent")}>
              <I.Gift width="12" height="12" /> Free trial
            </span>
          }
        >
          <div className="bp-plan-row">
            <div>
              <div className="bp-plan-name">{plan?.name}</div>
              <div className="bp-plan-sub">
                {monthly ? `Then ${monthly} after your trial` : null}
              </div>
            </div>
            <div className={"bp-count" + (soon ? " warn" : "")}>
              {daysLeft} <span>days left</span>
            </div>
          </div>
          <div className="bp-trialbar">
            <span style={{ width: progress + "%" }} />
          </div>
          {soon ? (
            <div className="bp-note warn">
              <I.Alert width="14" height="14" /> Trial ends in {daysLeft} days —
              first charge {price ? money(price.unitAmount) : ""} on{" "}
              {fmtStamp(endStamp)}.
            </div>
          ) : (
            <div className="bp-note">
              <I.Clock width="14" height="14" />
              <span>
                First charge on <b>{fmtStamp(endStamp)}</b>
                {defaultCard ? <> to {cardLabel(defaultCard)}</> : null}.
              </span>
            </div>
          )}
          <div className="bp-actions bp-actions-wrap">
            <button
              className="btn btn-ghost"
              onClick={() => history.push("/subscription-plans")}
            >
              Change plan
            </button>
            <button
              className="btn btn-text danger"
              style={{ marginLeft: "auto" }}
              onClick={() => setModal("cancel")}
            >
              Cancel trial
            </button>
          </div>
        </PanelShell>
        {modals}
      </>
    );
  }

  /* 3 — active */
  return (
    <>
      <PanelShell
        badge={
          <span className="badge badge-success">
            <span className="dot" style={{ background: "var(--success-500)" }} />{" "}
            Active
          </span>
        }
      >
        <div className="bp-plan-row">
          <div>
            <div className="bp-plan-name">{plan?.name}</div>
            <div className="bp-plan-sub">
              <b>{price ? money(price.unitAmount) : ""}</b>/mo · renews on{" "}
              {fmtStamp(endStamp)}
            </div>
          </div>
          <span className="badge badge-accent">Monthly</span>
        </div>

        <div className="bp-meta">
          <div>
            <div className="bp-meta-top">
              <span className="bp-meta-lbl">
                <I.MapPin width="13" height="13" /> Locations
              </span>
              <span className="bp-meta-val">
                {locationsLabel(plan?.allowedNumberOfBranches)}
              </span>
            </div>
            <button
              className="link"
              style={{ fontSize: 12 }}
              onClick={() => history.push("/branches")}
            >
              Manage locations
            </button>
          </div>
          <div>
            <div className="bp-meta-top">
              <span className="bp-meta-lbl">
                <I.Card width="13" height="13" /> Paying card
              </span>
            </div>
            <div className="bp-paycard">
              {defaultCard ? (
                <>
                  <span className="card-brand">
                    <CardBrand brand={defaultCard.brand} />
                  </span>
                  <span>{cardLabel(defaultCard)}</span>
                </>
              ) : (
                <span style={{ color: "var(--text-weak)" }}>
                  No card on file
                </span>
              )}
              <button
                className="link"
                style={{ marginLeft: "auto", fontSize: 12 }}
                onClick={() =>
                  setModal(
                    defaultCard && cards.length > 1 ? "changeDefault" : "addCard"
                  )
                }
              >
                Change
              </button>
            </div>
          </div>
        </div>

        <div className="divider" style={{ margin: "20px 0 16px" }} />
        <div className="bp-actions bp-actions-wrap">
          <button
            className="btn btn-ghost"
            onClick={() => history.push("/subscription-plans")}
          >
            Change plan
          </button>
          <button className="btn btn-ghost" onClick={openPortal}>
            <I.External width="13" height="13" /> Invoices &amp; billing history
          </button>
          <button
            className="btn btn-text danger"
            style={{ marginLeft: "auto" }}
            onClick={() => setModal("cancel")}
          >
            Cancel subscription
          </button>
        </div>
      </PanelShell>
      {modals}
    </>
  );
};

export default SubscriptionPanel;
